import { zodResolver as hookformZodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors, FieldValues, Resolver, ResolverOptions, ResolverResult } from 'react-hook-form'
import type { z } from 'zod'

/**
 * Zod v4 compatible resolver for react-hook-form.
 *
 * @hookform/resolvers/zod v3.x checks `error.errors` to detect ZodError,
 * but Zod v4 renamed this property to `error.issues`.
 * This wrapper patches thrown ZodErrors so the resolver can catch them.
 */
export function zodResolver<T extends z.ZodType>(
    schema: T,
    schemaOptions?: Parameters<typeof hookformZodResolver>[1],
    resolverOptions?: Parameters<typeof hookformZodResolver>[2]
): Resolver<any> {
    const resolver = hookformZodResolver(schema, schemaOptions, resolverOptions)

    return async (
        values: FieldValues,
        context: any,
        options: ResolverOptions<FieldValues>
    ): Promise<ResolverResult<FieldValues>> => {
        try {
            return await resolver(values, context, options)
        } catch (error: any) {
            // Zod v4: ZodError has .issues but not .errors
            // The upstream resolver only checks .errors, so it re-throws
            if (error?.issues && Array.isArray(error.issues)) {
                const { toNestErrors } = await import('@hookform/resolvers')
                const { appendErrors } = await import('react-hook-form')

                const errors: Record<string, any> = {}
                const zodErrors = [...error.issues]

                for (const issue of zodErrors) {
                    const path = issue.path.join('.')
                    if (!errors[path]) {
                        errors[path] = { message: issue.message, type: issue.code }
                    }

                    const validateAllFieldCriteria = options.criteriaMode === 'all'
                    if (validateAllFieldCriteria) {
                        const types = errors[path].types
                        const messages = types && types[issue.code]
                        errors[path] = appendErrors(
                            path,
                            validateAllFieldCriteria,
                            errors,
                            issue.code,
                            messages
                                ? ([] as string[]).concat(messages as string[], issue.message)
                                : issue.message
                        )
                    }
                }

                return {
                    values: {},
                    errors: toNestErrors(errors as FieldErrors, options),
                }
            }

            throw error
        }
    }
}
